import { useState, useEffect } from "react";

/** Custom hook for keeping state data synced with localStorage.
 *
 * This creates `item` as state and look in localStorage for current value
 * (if not found, defaults to `firstValue`)
 *
 * When `item` changes, effect re-runs:
 * - if new state is null, removes from localStorage
 * - else, updates localStorage
 *
 * To the component, this just acts like state that is also synced to/from
 * localStorage::
 *
 *   const [myThing, setMyThing] = useLocalStorage("myThing")
 */
function useLocalStorage<T>(key: string, firstValue: T | null = null): [T | null, React.Dispatch<React.SetStateAction<T | null>>] {
  // Get from localStorage and parse stored json or return firstValue
  const readValue = (): T | null => {
    const item = window.localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : firstValue;
  };

  // State to store our value
  const [storedValue, setStoredValue] = useState<T | null>(readValue);

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  useEffect(() => {
    if (storedValue === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(storedValue));
    }
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}

export default useLocalStorage;
